import i18n from 'scoreboard/components/i18n'
import _ from 'lodash'

// Views
import LinesAndBands from './analytics/lines-and-bands'

import TrendLine from './analytics/trend-line'
import SerieView from './serie/serie-view'
import Stack from './analytics/stack'

// Models
import ChartDesignerFeature from '../model/chart-designer-feature'

import ChartDesignerSerie from '../model/chart-designer-serie'
import ChartDesignerStack from '../model/chart-designer-stack'
import ChartDesignerTrendLine from '../model/chart-designer-trend-line'
import constants from 'scoreboard/components/constants'
/**
 * These properties are the same across all variations of the line and band types
 */
const AGGREGATION = 'aggregated-chart-data'
const AGGREGATION_PERCENTAGE = 'aggregated-chart-data-percentage'
const DEFAULT_LINES_AND_BANDS = {
	view: LinesAndBands,
	model: ChartDesignerFeature,
	lineSourceTypes: { // matches LineSourceType.kt
		CONSTANT: 'constant-number',
		SCORECARD_ITEM: 'scorecard-item',
		INITIATIVE: 'initiative',
		AGGREGATION,
		AGGREGATION_PERCENTAGE,
		/**
		 * @param {String} t a lineSourceType value
		 * @returns {boolean} true if it's an aggregation
		 */
		isAggregation: t => [AGGREGATION, AGGREGATION_PERCENTAGE].includes(t)
	},
	scopes: { // matches LinesAndBandsScope.kt
		ENTIRE_CHART: 'entire-chart',
		PER_BREAKDOWN1: 'per-breakdown1',
		PER_BREAKDOWN2: 'per-breakdown2',
		PER_SERIES: 'per-series'
	}
}

/**
 * Creates all the children needed for a line and band feature
 * @param properties additional properties to include with the line child
 */
function createPreconfiguredChildren(properties) {
	return [
		LinesAndBands.newFill(),
		_.extend({
			enabled: true,
			type: 'line',
			color: 'gray-medium',
			opacity: constants.DEFAULT_LINE_OPACITY
		}, properties),
		LinesAndBands.newFill()
	]
}

/**
 * Keyed by ChartDesignerFeatureType enum json strings
 */
const featureMetadata = {
	'lines-and-bands' : DEFAULT_LINES_AND_BANDS,

	// START preconfigured lines and bands - #36724 these all add the same thing
	// as the 'lines-and-bands' type above, but with some options preselected
	'average-line': _.extend(_.clone(DEFAULT_LINES_AND_BANDS), {
		defaultAttrs: () => {
			return {
				scope: 'entire-chart',
				children: createPreconfiguredChildren({
					aggregationType: 'average',
					label: i18n.get('scorecards.node.metric.aggregationType.average'),
					lineSourceType: 'aggregated-chart-data'
				})
			}
		}
	}),
	'maximum-line': _.extend(_.clone(DEFAULT_LINES_AND_BANDS), {
		defaultAttrs: () => {
			return {
				scope: 'entire-chart',
				children: createPreconfiguredChildren({
					aggregationType: 'maximum',
					label: i18n.get('datasets.maximum'),
					lineSourceType: 'aggregated-chart-data'
				})
			}
		}
	}),
	'minimum-line': _.extend(_.clone(DEFAULT_LINES_AND_BANDS), {
		defaultAttrs: () => {
			return {
				scope: 'entire-chart',
				children: createPreconfiguredChildren({
					aggregationType: 'minimum',
					label: i18n.get('datasets.minimum'),
					lineSourceType: 'aggregated-chart-data'
				})
			}
		}
	}),
	'goal-line': _.extend(_.clone(DEFAULT_LINES_AND_BANDS), {
		defaultAttrs: () => {
			return {
				scope: 'per-series',
				children: createPreconfiguredChildren({
					fieldName: 'scorecards.node.metric.threshold.goal',
					label: i18n.get('scorecards.node.metric.threshold.goal'),
					lineSourceType: 'scorecard-item',
					color: 'green-medium'
				})
			}
		}
	}),
	'scoring-bands': _.extend(_.clone(DEFAULT_LINES_AND_BANDS), {
		defaultAttrs: (type, chart, formResults) => {
			if (formResults) return formResults
			return {}
		}
	}),
	'constant-line': _.extend(_.clone(DEFAULT_LINES_AND_BANDS), {
		defaultAttrs: (type, chart, formResults) => {
			return {
				scope: 'entire-chart',
				children: createPreconfiguredChildren({
					constantNumber: Number(formResults),
					lineSourceType: 'constant-number'
				})
			}
		}
	}),
	// END preconfigured lines and bands

	'trend-line': {
		view: TrendLine,
		model: ChartDesignerTrendLine
	},
	'serie' : {
		view: SerieView,
		model: ChartDesignerSerie
	},
	'stack' : {
		view: Stack,
		model: ChartDesignerStack,
		defaultAttrs: (type, chart, formResults, axis) => {
			let plotType = type.split('-')[0]
			let fullHeight = null
			if (plotType === 'bar') {
				// Find the fullHeight option set on other bar stacks if any exist
				let stacks = chart.getFeatures({
					axis,
					type: 'stack'
				})
				stacks = _.filter(stacks, stack => stack.get('plotType') === 'bar')
				fullHeight = _.isEmpty(stacks) ? false : stacks[0].get('fullHeight')
			}

			return {
				type: 'stack',
				children: [],
				plotType,
				fullHeight
			}
		}
	}
}

export default featureMetadata
featureMetadata.__filename__ = 'charts/edit/feature-metadata.js'
